import React, { useEffect, useRef } from'react';

const ReserveReminderModal = ({ notification, onClose }) => {
 const hasPlayedVoiceRef = useRef(false);

 // Debug logging
 useEffect(() => {
 if (notification) {
 console.log('🔔 ReserveReminderModal rendered with notification:', notification);
 console.log('🔔 Notification keys:', Object.keys(notification));
 console.log('🔔 Reminder type:', notification.reminderType);
 console.log('🔔 Booking data:', notification.booking);
 } else {
 console.log('⚠️ ReserveReminderModal: No notification provided');
 }
 }, [notification]);

 // Play audio file when modal appears
 useEffect(() => {
 if (!notification || hasPlayedVoiceRef.current) return;

 let timeoutId = null;
 let audioElement = null;

 const playAudio = () => {
 try {
 const { reminderType } = notification;
 
 if (!reminderType) {
 console.warn('⚠️ No reminder type found, skipping audio playback');
 return;
 }

 // Determine if user is driver or regular user
 // Check localStorage for user info or check if we're on driver dashboard
 const userStr = localStorage.getItem('uttaapp_user');
 let isDriver = false;
 
 try {
 if (userStr) {
 const user = JSON.parse(userStr);
 isDriver = !!user.driverId;
 }
 } catch (e) {
 console.warn('⚠️ Could not parse user from localStorage');
 }
 
 // Also check current pathname as fallback
 if (!isDriver && window.location.pathname.includes('/driver')) {
 isDriver = true;
 }

 // Map reminder type to audio file
 let audioPath ='';
 
 switch (reminderType) {
 case'5hour':
 audioPath = isDriver ?'/driver/reserve5hours.mp3':'/user/user reserve5hour.mp3';
 break;
 case'1hour':
 audioPath = isDriver ?'/driver/reserve1hour.mp3':'/user/user reserve1hour.mp3';
 break;
 case'30min':
 // Note: Driver file has a space in the name"reserve 30min.mp3"audioPath = isDriver ?'/driver/reserve 30min.mp3':'/user/user reserved30min.mp3';
 break;
 case'10min':
 audioPath = isDriver ?'/driver/10minreserve.mp3':'/user/user reserve10min.mp3';
 break;
 default:
 console.warn(`⚠️ Unknown reminder type: ${reminderType}`);
 return;
 }
 
 // URL encode the path to handle spaces in filenames
 audioPath = encodeURI(audioPath);

 console.log(`🔊 Playing audio for ${isDriver ?'driver':'user'}: ${audioPath}`);
 console.log(`🔊 Reminder type: ${reminderType}`);

 // Create audio element
 audioElement = new Audio(audioPath);
 audioElement.volume = 1.0;
 audioElement.preload ='auto';

 audioElement.onloadeddata = () => {
 console.log('✅ Audio loaded successfully:', audioPath);
 };

 audioElement.onerror = (err) => {
 console.error('❌ Audio loading/playback error:', err);
 console.error('❌ Audio path:', audioPath);
 console.error('❌ Error details:', audioElement.error);
 };

 audioElement.onended = () => {
 console.log('🔊 Audio playback completed');
 hasPlayedVoiceRef.current = true;
 };

 // Small delay to ensure modal is visible
 timeoutId = setTimeout(() => {
 const playPromise = audioElement.play();
 
 if (playPromise !== undefined) {
 playPromise
 .then(() => {
 console.log('✅ Audio playback started successfully');
 hasPlayedVoiceRef.current = true;
 })
 .catch((error) => {
 console.error('❌ Error playing audio:', error);
 console.error('❌ Audio path:', audioPath);
 // User interaction might be required - try again on user interaction
 console.log('💡 Audio play failed - may require user interaction');
 });
 }
 }, 300);
 } catch (error) {
 console.error('❌ Error in playAudio:', error);
 }
 };

 playAudio();

 // Cleanup: stop audio when component unmounts
 return () => {
 if (timeoutId) {
 clearTimeout(timeoutId);
 }
 if (audioElement) {
 audioElement.pause();
 audioElement.currentTime = 0;
 audioElement = null;
 }
 hasPlayedVoiceRef.current = false;
 };
 }, [notification]);

 if (!notification) {
 console.log('⚠️ ReserveReminderModal: Returning null - no notification');
 return null;
 }

 const { title, message, scheduledTime, scheduledDate, reminderType, booking } = notification;

 // Get reminder type display text
 const getReminderTypeText = (type) => {
 switch (type) {
 case'5hour':
 return'5 Hours';
 case'1hour':
 return'1 Hour';
 case'30min':
 return'30 Minutes';
 case'10min':
 return'10 Minutes';
 default:
 return'Soon';
 }
 };

 // Format time display
 const displayTime = scheduledTime || (booking?.scheduledStartTime ||'');
 const displayDate = scheduledDate || (booking?.scheduledStartDate 
 ? new Date(booking.scheduledStartDate).toLocaleDateString('en-IN', {
 day:'2-digit',
 month:'short',
 year:'numeric'})
 :'');

 return (
 <div className="fixed inset-0 flex items-center justify-center bg-slate-950/60 p-3 backdrop-blur-sm z-[10006]"style={{ zIndex: 10006 }}>
 <div className="w-full max-w-[960px] overflow-hidden rounded-[30px] border border-white/70 bg-[linear-gradient(180deg,#ffffff_0%,#f6fbf8_100%)] shadow-[0_34px_100px_-46px_rgba(15,23,42,0.55)] animate-slideUp">
 {/* Header */}
 <div className="relative overflow-hidden bg-[linear-gradient(135deg,#0f766e_0%,#16a34a_52%,#7dd3fc_100%)] px-5 py-3.5 text-white">
 <div className="absolute -right-10 -top-10 h-32 w-32 rounded-full bg-white/15 blur-3xl"/>
 <div className="absolute -left-8 bottom-0 h-24 w-24 rounded-full bg-emerald-200/20 blur-2xl"/>
 <div className="relative flex items-start justify-between gap-3">
 <div className="flex items-start gap-3">
 <div className="flex h-12 w-12 items-center justify-center rounded-[18px] bg-white/18 backdrop-blur-md">
 <svg className="h-6 w-6 text-white"fill="none"stroke="currentColor"viewBox="0 0 24 24">
 <path strokeLinecap="round"strokeLinejoin="round"strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
 </svg>
 </div>
 <div>
 <p className="text-[11px] font-bold uppercase tracking-[0.24em] text-emerald-100">Reserve Reminder</p>
 <h2 className="mt-1.5 text-xl font-black tracking-[-0.03em] text-white">{title ||'UttaApp Reminder'}</h2>
 <p className="mt-1.5 max-w-xs text-xs leading-5 text-white/85">Your reserved ride is coming up soon. Keep this trip details panel handy.</p>
 </div>
 </div>
 <button
 onClick={onClose}
 className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-white/20 bg-white/10 text-white transition-colors hover:bg-white/20"aria-label="Close">
 <svg className="h-5 w-5"fill="none"stroke="currentColor"viewBox="0 0 24 24">
 <path strokeLinecap="round"strokeLinejoin="round"strokeWidth={2} d="M6 18L18 6M6 6l12 12"/>
 </svg>
 </button>
 </div>
 </div>

 {/* Content */}
 <div className="space-y-3 p-4">
 {/* Reminder Badge */}
 {reminderType && (
 <div className="inline-flex items-center gap-2 rounded-full border border-emerald-100 bg-emerald-50 px-3.5 py-1.5 text-xs font-bold text-emerald-700">
 <span className="h-2.5 w-2.5 rounded-full bg-emerald-500"/>
 {getReminderTypeText(reminderType)} Before Scheduled Time
 </div>
 )}

 {/* Message */}
 <div className="rounded-[22px] border border-slate-100 bg-white p-4 shadow-[0_18px_40px_-34px_rgba(15,23,42,0.14)]">
 <p className="text-sm leading-6 text-slate-700">{message}</p>
 </div>

 {/* Booking Details */}
 {booking && (
 <div className="grid gap-3 sm:grid-cols-2">
 <div className="rounded-[24px] border border-emerald-100 bg-[linear-gradient(135deg,#f0fdf4_0%,#ecfeff_100%)] p-4 shadow-[0_18px_44px_-34px_rgba(22,163,74,0.4)] sm:col-span-2">
 <div className="flex items-start gap-3">
 <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-white text-emerald-600 shadow-sm">
 <svg className="h-5 w-5"fill="none"stroke="currentColor"viewBox="0 0 24 24">
 <path strokeLinecap="round"strokeLinejoin="round"strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/>
 </svg>
 </div>
 <div className="min-w-0 flex-1">
 <p className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-700/80">Scheduled Time</p>
 <p className="mt-1.5 text-base font-black tracking-[-0.02em] text-slate-900">
 {displayDate} {displayTime &&`at ${displayTime}`}
 </p>
 <p className="mt-1 text-xs text-slate-600">Please be ready a few minutes before the trip begins.</p>
 </div>
 </div>
 </div>

 {(booking.pickupLocation || booking.pickupLocationName) && (
 <div className="flex items-start gap-3 rounded-[22px] border border-sky-100 bg-sky-50/80 p-4">
 <svg className="h-5 w-5 text-sky-600"fill="none"stroke="currentColor"viewBox="0 0 24 24">
 <path strokeLinecap="round"strokeLinejoin="round"strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/>
 <path strokeLinecap="round"strokeLinejoin="round"strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/>
 </svg>
 <div className="min-w-0 flex-1">
 <p className="text-xs font-bold uppercase tracking-[0.2em] text-sky-700/80">Pickup Point</p>
 {/* Priority: pickupLocation (Google Places address from"Location Address"input) > pickupLocationName (pincode search from"Location Name"input) */}
 <p className="mt-1.5 text-sm font-bold leading-6 text-slate-900">{booking.pickupLocation || booking.pickupLocationName}</p>
 {booking.pickupLandmark && (
 <p className="text-sm text-gray-600 mt-1">📍 {booking.pickupLandmark}</p>
 )}
 {booking.pickupPincode && (
 <p className="mt-3 inline-flex rounded-full border border-sky-200 bg-white px-3 py-1 text-xs font-bold tracking-[0.14em] text-sky-700">PIN {booking.pickupPincode}</p>
 )}
 </div>
 </div>
 )}

 {booking.dropoffLocation && (
 <div className="flex items-start gap-3 rounded-[22px] border border-violet-100 bg-violet-50/80 p-4">
 <svg className="h-5 w-5 text-violet-600"fill="none"stroke="currentColor"viewBox="0 0 24 24">
 <path strokeLinecap="round"strokeLinejoin="round"strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V7m0 0L9 4"/>
 </svg>
 <div className="min-w-0 flex-1">
 <p className="text-xs font-bold uppercase tracking-[0.2em] text-violet-700/80">Drop-off Point</p>
 <p className="mt-1.5 text-sm font-bold leading-6 text-slate-900">{booking.dropoffLocation}</p>
 {booking.dropoffLandmark && (
 <p className="text-sm text-gray-600 mt-1">📍 {booking.dropoffLandmark}</p>
 )}
 </div>
 </div>
 )}

 {/* Driver Information */}
 {booking.driverName && (
 <div className="flex items-start gap-3 rounded-[22px] border border-amber-100 bg-amber-50/80 p-4">
 <svg className="h-5 w-5 text-amber-600"fill="none"stroke="currentColor"viewBox="0 0 24 24">
 <path strokeLinecap="round"strokeLinejoin="round"strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
 </svg>
 <div className="min-w-0 flex-1">
 <p className="text-xs font-bold uppercase tracking-[0.2em] text-amber-700/80">Assigned Driver</p>
 <p className="mt-1.5 text-sm font-bold text-slate-900">{booking.driverName}</p>
 {booking.vehicleType && (
 <p className="text-sm text-gray-600 mt-1">🚗 {booking.vehicleType}</p>
 )}
 </div>
 </div>
 )}
 </div>
 )}
 </div>

 {/* Footer */}
 <div className="border-t border-slate-100 bg-white/90 px-5 py-4">
 <button
 onClick={onClose}
 className="inline-flex w-full items-center justify-center rounded-[18px] bg-[linear-gradient(135deg,#0f766e_0%,#16a34a_100%)] px-6 py-3 text-sm font-bold tracking-[0.12em] text-white shadow-[0_20px_38px_-24px_rgba(22,163,74,0.7)] transition-transform duration-200 hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2">
 Got it
 </button>
 </div>
 </div>
 </div>
 );
};

export default ReserveReminderModal;







